import { Reducer } from 'interfaces'
import { Accomplishment, BountyAppState, RedemptionStepNumber } from 'interfaces/bounty'
import {
  ADD_ACCOMPLISHMENT,
  GET_REDEMPTIONS,
  GET_REWARDS,
  RESET_ACCOMPLISHMENTS,
  SET_REDEMPTION_STEP_NUMBER,
  TOGGLE_BOUNTY_BLOCK_LOADING,
  TOGGLE_DISMISS_REWARD,
  TOGGLE_REWARD_DETAILS_MODAL,
  START_REDEMPTION_FLOW,
} from 'actions/bountyActions'
import { isRequestAction, isSuccessAction } from 'helpers/actionsHelpers'
import { needDiscardData, REWARDS_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { combineReducers } from 'redux'
import { DEFAULT_REDEMPTIONS_SORTING, REDEMPTION_LAST_STEP_NUMBER } from 'constants/bounty'

const DEFAULT_STATE: BountyAppState = {
  reward: {
    isModalOpened: false,
    modalType: 'cash',
    items: {
      isLoading: false,
      page: 1,
      pageSize: 5,
      filter: 'all',
      dismissingId: null,
    },
  },
  redemption: {
    isCompleted: false,
    stepNumber: 0,
    items: {
      isLoading: false,
      page: 1,
      pageSize: 5,
      sorting: DEFAULT_REDEMPTIONS_SORTING,
    },
  },
  accomplishments: [],
}

const isRedemptionFormCompletedReducer: Reducer<BountyAppState['redemption']['isCompleted']> = (state = DEFAULT_STATE.redemption.isCompleted, action) => {
  if (action.type === SET_REDEMPTION_STEP_NUMBER && action.payload === REDEMPTION_LAST_STEP_NUMBER) {
    return true
  }

  if (action.type === START_REDEMPTION_FLOW) {
    return false
  }

  return state
}

const rewardItemsReducer: Reducer<BountyAppState['reward']['items']> = (state = DEFAULT_STATE.reward.items, action) => {
  if (needDiscardData(REWARDS_DISCARD_TRIGGERS)(action)) {
    return DEFAULT_STATE.reward.items
  }

  if (action.type === TOGGLE_BOUNTY_BLOCK_LOADING && action.payload.block === 'rewards-details') {
    return {
      ...state,
      isLoading: action.payload.toggleValue,
    }
  }

  switch (action.type) {
    case GET_REWARDS: {
      if (isSuccessAction(action)) {
        return {
          ...state,
          ...action.payload.params,
        }
      }

      return state
    }

    case TOGGLE_REWARD_DETAILS_MODAL: {
      return {
        ...state,
        isModalOpened: action.payload,
      }
    }

    case TOGGLE_DISMISS_REWARD: {
      if (isRequestAction(action)) {
        return {
          ...state,
          dismissingId: action.payload.params.id,
        }
      }

      return {
        ...state,
        dismissingId: DEFAULT_STATE.reward.items.dismissingId,
      }
    }

    default:
      return state
  }
}

const rewardModalReducer: Reducer<boolean> = (state = DEFAULT_STATE.reward.isModalOpened, action) => {
  if (action.type === TOGGLE_REWARD_DETAILS_MODAL) {
    return action.payload !== false
  }

  return state
}

const rewardModalTypeReducer: Reducer<BountyAppState['reward']['modalType']> = (state = DEFAULT_STATE.reward.modalType, action) => {
  if (action.type === TOGGLE_REWARD_DETAILS_MODAL) {
    return action.payload === false
      ? DEFAULT_STATE.reward.modalType
      : action.payload
  }

  return state
}

const redemptionStepNumberReducer: Reducer<RedemptionStepNumber> = (state = DEFAULT_STATE.redemption.stepNumber, action) => {
  if (action.type === SET_REDEMPTION_STEP_NUMBER) {
    const newRedemptionStepNumber = action.payload // null resets redemption process
    return newRedemptionStepNumber === null
      ? DEFAULT_STATE.redemption.stepNumber
      : state > newRedemptionStepNumber ? state : newRedemptionStepNumber
  }

  return state
}

const redemptionItemsReducer: Reducer<BountyAppState['redemption']['items']> = (state = DEFAULT_STATE.redemption.items, action) => {
  if (action.type === GET_REDEMPTIONS && isSuccessAction(action)) {
    return {
      ...state,
      ...action.payload.params,
    }
  }

  if (action.type === TOGGLE_BOUNTY_BLOCK_LOADING && action.payload.block === 'redemptions') {
    return {
      ...state,
      isLoading: action.payload.toggleValue,
    }
  }

  return state
}

const accomplishmentsReducer: Reducer<Accomplishment[]> = (state = [], action) => {
  switch (action.type) {
    case ADD_ACCOMPLISHMENT:
      return [...state, action.payload]

    case RESET_ACCOMPLISHMENTS:
      return []

    default:
      return state
  }
}

export default combineReducers<BountyAppState>({
  reward: combineReducers({
    isModalOpened: rewardModalReducer,
    modalType: rewardModalTypeReducer,
    items: rewardItemsReducer,
  }),
  redemption: combineReducers({
    stepNumber: redemptionStepNumberReducer,
    isCompleted: isRedemptionFormCompletedReducer,
    items: redemptionItemsReducer,
  }),
  accomplishments: accomplishmentsReducer,
})
